import deepcopy from 'deepcopy';

import { measureText, mergeOptionsRecords } from '../libSupport';
import { setButtonMarginLeft } from '../IconButtonV2';

import { FormFieldRecord, CustomValidatorCallback, FormFieldType, FormFieldSizingRecord } from '../../interfaces/lib-api-interfaces';

import app from '../../appData';

export const isValidEmail = (email: string) => {
    return email && (/(.+)@(.+){2,}\.(.+){2,}/).test(email);
}

/* following functions are public:
    getFormFields
    getField
    getFormValues
    getValue
    setValue
    getError
    deleteError
    setFormFieldsAndValues
    validateForm
*/

// the master record property is always the form id; the child record property is the field name; the child record value is string for error or field value for form
type FormOrErrorState = Record<string, Record<string, any>>;
// property is form id; value is the fields belonging to that form
type FieldsState = Record<string, FormFieldRecord[]>;

/*
    values: FormOrErrorState;
    fields: FieldsState;
    errors: FormOrErrorState;
*/

const valuesKey = "formValues";
const fieldsKey = "formFields";
const errorsKey = "formErrors";
const rerenderKey = "formMgr";

export const initForms = (store: { setContext: (key: string, data: any) => void }) => {
    // console.log("initForms:", store, "; fields:");
    // store.setContext(valuesKey, {});
    // store.setContext(fieldsKey, {});
    // store.setContext(errorsKey, {});
}

class FormMgr {
    setContext: (key: string, data: any) => void;

    constructor(setContext: (key: string, data: any) => void) {
        if (!sessionStorage.getItem(fieldsKey)) {
            this.clearForms();
        }
        this.setContext = setContext;
    }
    _getSessionStore = (key: string): any => {
        const rawData: string | null = sessionStorage.getItem(key);
        return rawData ? JSON.parse(rawData) : null;
    }
    _setSessionStore = (key: string, data: any, doNotRerender?: boolean) => {
        const currStringified = sessionStorage.getItem(key);
        const newstringified = JSON.stringify(data);
        if (currStringified !== null && currStringified === newstringified) {
            return;
        }
        sessionStorage.setItem(key, newstringified);
        if (!doNotRerender) {
            this.setContext(rerenderKey, Date.now());
        }
    }
    // following is called upon route change to simulate keeping session store in global context
    clearForms = () => {
        this._setSessionStore(valuesKey, {}, true);
        this._setSessionStore(fieldsKey, {}, true);
        this._setSessionStore(errorsKey, {}, true);
    }

    //------- CONTEXT ------------
    _getContext = (key: string, isCopy: boolean): FormOrErrorState | FieldsState => {
        const state = this._getSessionStore(key);
        if (isCopy) {
            return state ? deepcopy(state) : null;
        }
        return state;
    }
    _setContext = (key: string, data: FormOrErrorState | FieldsState) => {
        this._setSessionStore(key, data);
    }

    //------- FIELDS ------------
    getFormFields = (formId: string): FormFieldRecord[] | null => {
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        return fields ? deepcopy(fields[formId]) : null;
    }
    getFieldByName = (formId: string, fieldName: string): FormFieldRecord | null => {
        const field = this._findField(formId, fieldName);
        return field ? deepcopy(field) : null;
    }
    // use following if field might be a header or linebreak
    getFieldByIndex = (formId: string, index: number): FormFieldRecord | null => {
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        return fields[formId] && index < fields[formId].length ? deepcopy(fields[formId][index]) : null;
    }
    _findField = (formId: string, fieldName: string): FormFieldRecord | undefined => {
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        return fields[formId].find(field => field.name === fieldName);
    }

    //------- VALUES ------------
    getFormValues = (formId: string): Record<string, any> => {
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        const formValues = deepcopy(values[formId]) as Record<string, any>;
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        for (const fieldName in formValues) {
            if (fields[formId].find(field => field.name === fieldName)?.type === FormFieldType.header) {
                delete formValues[fieldName];
            }
        }
        return formValues;
    }
    // return current values with media fields recombined into MediaRecord objects
    getFormValuesWithMedia = (formId: string): Record<string, any> => {
        const rawValues = this.getFormValues(formId);
        return FormMgr.combineMediaValues(rawValues);
    }

    static combineMediaValues = (rawValues: Record<string, any>) => {
        const values = {} as Record<string, any>;
        for (const fieldName in rawValues) {
            if (fieldName.endsWith("-ipad")) {
                const name = fieldName.replace("-ipad", '');
                values[name] = { default: rawValues[name], ipad: rawValues[name + "-ipad"], mobile: rawValues[name + "-mobile"] };
            } else if (!fieldName.endsWith("-mobile")) {
                values[fieldName] = rawValues[fieldName];
            }
        }
        return values;
    }

    getValue = (formId: string, fieldName: string): any => {
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        if (!(formId in values) || !(fieldName in values[formId])) {
            return '';
        }
        return values[formId][fieldName];
    }
    // returns updated form values object for given form
    setValue = (formId: string, fieldName: string, value: any): Record<string, any> => {
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        values[formId][fieldName] = value;
        this._setSessionStore(valuesKey, values);

        return values[formId];
    }

    //------- ERRORS ------------
    getError = (formId: string, fieldName: string): string => {
        const errors = this._getSessionStore(errorsKey) as FormOrErrorState;
        if (!errors[formId] || !errors[formId][fieldName]) {
            return '';
        }
        return errors[formId][fieldName];
    }
    deleteError = (formId: string, fieldName: string) => {
        const errors = this._getSessionStore(errorsKey) as FormOrErrorState;
        if (errors[formId] && fieldName in errors[formId]) {
            delete errors[formId][fieldName];
            this._setSessionStore(errorsKey, errors);
        }
    }
    _setFormErrors = (formId: string, formErrors: Record<string, string>) => {
        const errors = this._getSessionStore(errorsKey) as FormOrErrorState;
        errors[formId] = formErrors;
        this._setSessionStore(errorsKey, errors);
    }

    //------- FIELDS AND VALUES ------------
    // initialize fields and values -- call this once from form or useEffect
    _defaultFieldSizes: FormFieldSizingRecord = {
        height: 24, marginLeft: 16, marginTop: 8
    }

    // labelFontSize required if field.size.widthFromLabel true
    initFieldsAndValues = (formId: string, initialFields: FormFieldRecord[], initialValues?: Record<string, any>, labelFontSize?: number) => {
        const fields: FormFieldRecord[] = [];
        const values = {} as Record<string, any>;
        for (const field of initialFields) {
            const fieldName = field.name ?? '';
            const formattedField = deepcopy(field) as FormFieldRecord;
            if (field.type === FormFieldType.digitsOnly && (!field.validator || !field.validator.maxLength)) {
                const err = field.name! + ": field.validator.maxLength required on digitsOnly fields";
                throw err;
            }
            formattedField.size = mergeOptionsRecords([this._defaultFieldSizes, field.size]) as FormFieldSizingRecord;
            if (field.fixedWidth) {
                formattedField.size.widthPx = field.fixedWidth;
            }
            if (formattedField.size.widthFromLabel) {
                formattedField.size.widthPx = measureText(field.label!, labelFontSize + "px " + (app.themes.sansFonts ?? "Arial,Helvetica,sans-serif")) + 4;
            } else {
                if (field.width) {
                    formattedField.size.widthPct = field.width;
                }
                if (field.fixedWidth) {
                    formattedField.size.widthPx = field.fixedWidth;
                }
                if (!("widthPct" in formattedField.size) && !("widthPx" in formattedField.size)) {
                    formattedField.size.widthPct = 48;
                }
            }
            if (!field.type) {
                formattedField.type = FormFieldType.text;
            }
            if (field.visible === undefined) {
                formattedField.visible = true;
            }
            if (field.button) {
                setButtonMarginLeft(field.button, 16);
            }
            fields.push(formattedField);

            // field has been formatted; now see if we have an initial value
            if (initialValues && (fieldName in initialValues)) {
                // initial value was passed
                values[fieldName] = initialValues[fieldName];
            } else if ("initialValue" in field) {
                // no value passed but we got one in the field object
                values[fieldName] = field.initialValue;
            }
            // note: if we are unable to set an initial value, _cleanValues will default it based on type
        }
        this.setFormFieldsAndValues(formId, fields, values);
    }
    // this is for SamForm to call after initializing fields
    setFormFieldsAndValues = (formId: string, formFields: FormFieldRecord[], formValues: Record<string, any>) => {
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        fields[formId] = deepcopy(formFields);
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        values[formId] = this._cleanValues(this._expandMediaValues(formValues), fields[formId]);
        this._setSessionStore(fieldsKey, fields);
        this._setSessionStore(valuesKey, values);
    }
    // use this to change values on the fly from anywhere; pass initialValues to SamForm, THEN call this after it has initialized fields
    // media values are expanded from MediaValueRecord to 3 values
    setFormValues = (formId: string, formValues: Record<string, any>) => {
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        values[formId] = this._cleanValues(this._expandMediaValues(formValues), fields[formId]);
        this._setSessionStore(valuesKey, values);
    }
    _expandMediaValues = (values: Record<string, any>): Record<string, any> => {
        const newValues = {} as Record<string, any>;
        for (const field in values) {
            if (values[field]) {
                if (typeof values[field] === "object") {
                    newValues[field] = values[field].default;
                    newValues[field + "-ipad"] = values[field].ipad;
                    newValues[field + "-mobile"] = values[field].mobile;
                } else {
                    newValues[field] = values[field];
                }
            }
        }
        return newValues;
    }
    // eliminate all null values so controlled forms will work; return copy of values
    _cleanValues = (formValues: Record<string, any>, formFields: FormFieldRecord[]): Record<string, any> => {
        const clean = {} as Record<string, any>;
        for (const field of formFields) {
            if (field.name) {
                if (!formValues[field.name]) {
                    if (field.type === FormFieldType.checkbox) {
                        clean[field.name] = false;
                    } else if (field.type === FormFieldType.int || field.type === FormFieldType.decimal || field.type === FormFieldType.fixedPoint) {
                        clean[field.name] = 0;
                    } else {
                        clean[field.name] = '';
                    }
                } else {
                    clean[field.name] = formValues[field.name];
                }
            }
        }
        return clean;
    }

    // if media values are found they are combined into MediaValueRecords
    static extractValuesFromFields = (fields: FormFieldRecord[]): Record<string, any> => {
        const record: Record<string, any> = {};
        for (const field of fields) {
            if (field.name) {
                record[field.name] = field.initialValue;
            }
        }
        return this.combineMediaValues(record);
    }



    //------- VALIDATE ------------
    // return true if form validates
    validateForm = (formId: string, customValidator: CustomValidatorCallback | null = null): boolean => {
        const fields = this._getSessionStore(fieldsKey) as FieldsState;
        const values = this._getSessionStore(valuesKey) as FormOrErrorState;
        const newErrors = this._validateAllFields(formId, fields[formId], values[formId], customValidator);
        this._setFormErrors(formId, newErrors);
        return !Object.keys(newErrors).length;
    }
    _validateAllFields = (formId: string, fields: FormFieldRecord[], values: Record<string, any>, customValidator: CustomValidatorCallback | null): Record<string, string> => {
        const newErrors: Record<string, string> = {};
        for (const field of fields) {
            if (field.name) {
                let errorMessages = this._validateField(formId, field, values, customValidator);      // this combines standard validation with custom user validation (if any)
                console.log(field.name + "-->" + errorMessages[0]);
                if (errorMessages.length > 0) {
                    newErrors[field.name] = errorMessages[0];
                }
            }
        }
        return newErrors;
    }
    // return an array of error messages
    // return empty array if no errors
    _validateField = (formId: string, field: FormFieldRecord, values: Record<string, any>, customValidator: CustomValidatorCallback | null) => {
        let messages = [];
        if (field.name) {
            let value = values[field.name];
            const v = field.validator;
            // start with caller provided validators
            if (v && v.required && !value) {
                messages.push(field.label + " is required");
                return messages;
            }
            if (v && v.minLength && (!value || value.length < v.minLength)) {
                messages.push("Must be at least " + v.minLength + " characters");
            }
            if (field.name.includes("email") && value && !isValidEmail(value)) {
                messages.push("Please enter a valid email address");
            }
            if (messages.length === 0 && customValidator) {
                const msg = customValidator(field.name, values);
                if (msg) {
                    messages.push(msg);
                }
            }
        }
        return messages;
    }
}
export default FormMgr;

